<template>
	<div>
		<!-- 顶部用户信息 -->
		<div class="sg-portal-header">
			<div class="sg-portal-user">
				<div class="sg-portal-avatar">{{ userInitial }}</div>
				<div>
					<div class="sg-portal-username">{{ userName }}</div>
					<div class="sg-portal-role">{{ roleLabel }}</div>
				</div>
			</div>
			<div class="sg-portal-balance">
				<div class="sg-portal-balance-value">{{ myStats.balance || 0 }}</div>
				<div class="sg-portal-balance-label">积分余额</div>
			</div>
		</div>

		<!-- 统计概览 -->
		<div class="sg-stats">
			<div class="sg-stat-card">
				<div class="sg-stat-value">{{ myStats.suggestion_count || 0 }}</div>
				<div class="sg-stat-label">我的建议</div>
			</div>
			<div class="sg-stat-card">
				<div class="sg-stat-value">{{ reviewStats.pending || 0 }}</div>
				<div class="sg-stat-label">待评审</div>
			</div>
			<div class="sg-stat-card">
				<div class="sg-stat-value">{{ myStats.total_earned || 0 }}</div>
				<div class="sg-stat-label">总获得</div>
			</div>
		</div>

		<!-- 底部 Tab 导航 -->
		<div class="sg-portal-tabs">
			<div class="sg-portal-tab" :class="{ active: mainTab === 'suggestions' }" @click="switchMainTab('suggestions')">
				<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
				<span>我的建议</span>
			</div>
			<div class="sg-portal-tab" :class="{ active: mainTab === 'review' }" @click="switchMainTab('review')">
				<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
				<span>评审</span>
				<span v-if="reviewStats.pending > 0" class="sg-tab-badge sg-badge-danger" style="position:absolute;top:-2px;right:-4px;">{{ reviewStats.pending }}</span>
			</div>
			<div class="sg-portal-tab" :class="{ active: mainTab === 'leaderboard' }" @click="switchMainTab('leaderboard')">
				<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 15l-2 5-3-1 1-5.5-3.5-2 1-3.5L6 5l3-2 3 2 3-2 3 2-1 3.5 1.5 3.5-3.5 2 1 5.5-3 1z"/></svg>
				<span>排行榜</span>
			</div>
			<div class="sg-portal-tab" :class="{ active: mainTab === 'redemption' }" @click="switchMainTab('redemption')">
				<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/></svg>
				<span>兑换</span>
			</div>
		</div>

		<!-- 内容区 -->
		<div class="sg-portal-content">

			<!-- ===== 我的建议 ===== -->
			<template v-if="mainTab === 'suggestions'">
				<div class="sg-sub-tabs">
					<div class="sg-sub-tab" :class="{ active: subTab === 'my_suggestions' }" @click="switchSubTab('my_suggestions')">我的建议</div>
					<div class="sg-sub-tab" :class="{ active: subTab === 'points' }" @click="switchSubTab('points')">积分明细</div>
					<div class="sg-sub-tab" :class="{ active: subTab === 'my_redemptions' }" @click="switchSubTab('my_redemptions')">兑换记录</div>
				</div>

				<div v-if="contentLoading" class="sg-loading"><div class="sg-spinner"></div><p>加载中...</p></div>

				<template v-else-if="subTab === 'my_suggestions'">
					<div v-if="!suggestions.length" class="sg-empty"><div class="sg-empty-icon">&#128221;</div>暂无建议记录</div>
					<div v-for="s in suggestions" :key="s.name" class="sg-card" @click="openSuggestionDetail(s)" style="cursor:pointer;">
						<div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:8px;">
							<div style="font-size:15px;font-weight:500;color:var(--text-1);flex:1;">{{ s.title }}</div>
							<span class="sg-status-pill" :style="getStatusStyle(s.status)">{{ getStatusText(s.status) }}</span>
						</div>
						<div style="margin-bottom:6px;"><span class="sg-category-tag active" style="cursor:default;">{{ s.category }}</span></div>
						<div style="display:flex;justify-content:space-between;font-size:12px;color:var(--text-3);">
							<span>{{ formatDate(s.creation) }}</span>
							<span v-if="s.total_score != null">得分：{{ s.total_score }}</span>
						</div>
					</div>
				</template>

				<template v-else-if="subTab === 'points'">
					<div v-if="!pointLogs.length" class="sg-empty"><div class="sg-empty-icon">&#128176;</div>暂无积分记录</div>
					<div v-for="log in pointLogs" :key="log.name" class="sg-point-log">
						<div class="sg-point-log-info">
							<div class="sg-point-log-type">{{ log.point_type }}</div>
							<div class="sg-point-log-remark" v-if="log.remark">{{ log.remark }}</div>
							<div class="sg-point-log-time">{{ formatDate(log.creation) }}</div>
						</div>
						<div class="sg-point-log-value" :class="log.points >= 0 ? 'plus' : 'minus'">
							{{ log.points >= 0 ? '+' : '' }}{{ log.points }}
						</div>
					</div>
				</template>

				<template v-else-if="subTab === 'my_redemptions'">
					<div v-if="!myRedemptions.length" class="sg-empty"><div class="sg-empty-icon">&#127873;</div>暂无兑换记录</div>
					<div v-for="r in myRedemptions" :key="r.name" class="sg-card">
						<div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:8px;">
							<div style="font-size:15px;font-weight:500;color:var(--text-1);">{{ r.item_name }}</div>
							<span class="sg-status-pill" :style="getStatusStyle(r.status)">{{ getStatusText(r.status) }}</span>
						</div>
						<div style="display:flex;justify-content:space-between;font-size:13px;color:var(--text-2);">
							<span>数量：{{ r.quantity }}</span>
							<span style="color:var(--warning);font-weight:500;">-{{ r.points_cost }} 积分</span>
						</div>
						<div style="font-size:12px;color:var(--text-3);margin-top:4px;" v-if="r.redemption_date">{{ formatDate(r.redemption_date) }}</div>
					</div>
				</template>
			</template>

			<!-- ===== 评审 ===== -->
			<template v-if="mainTab === 'review'">
				<div class="sg-sub-tabs">
					<div class="sg-sub-tab" :class="{ active: reviewTab === 'pending' }" @click="switchReviewTab('pending')">
						待评审<span v-if="reviewStats.pending > 0" class="sg-tab-badge sg-badge-danger" style="margin-left:4px;">{{ reviewStats.pending }}</span>
					</div>
					<div class="sg-sub-tab" :class="{ active: reviewTab === 'completed' }" @click="switchReviewTab('completed')">
						已评审<span v-if="reviewStats.completed > 0" class="sg-tab-badge sg-badge-success" style="margin-left:4px;">{{ reviewStats.completed }}</span>
					</div>
					<div class="sg-sub-tab" :class="{ active: reviewTab === 'skipped' }" @click="switchReviewTab('skipped')">
						已跳过<span v-if="reviewStats.skipped > 0" class="sg-tab-badge sg-badge-muted" style="margin-left:4px;">{{ reviewStats.skipped }}</span>
					</div>
				</div>

				<div v-if="contentLoading" class="sg-loading"><div class="sg-spinner"></div><p>加载中...</p></div>
				<div v-else>
					<div v-if="!reviews.length" class="sg-empty"><div class="sg-empty-icon">&#128203;</div>{{ reviewEmptyText }}</div>
					<div v-for="r in reviews" :key="r.name" class="sg-card" style="cursor:pointer;" @click="openReview(r)">
						<div style="display:flex;justify-content:space-between;align-items:flex-start;">
							<div style="flex:1;min-width:0;">
								<div style="font-size:16px;font-weight:600;color:var(--text-1);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">{{ r.suggestion_title }}</div>
								<div style="display:flex;align-items:center;margin-top:6px;gap:8px;">
									<span class="sg-badge sg-badge-purple">{{ r.suggestion_category }}</span>
									<span style="font-size:12px;color:var(--text-3);">{{ formatDate(r.creation) }}</span>
								</div>
							</div>
							<div style="text-align:right;flex-shrink:0;margin-left:12px;display:flex;flex-direction:column;align-items:flex-end;gap:4px;">
								<div v-if="r.total_score != null" style="font-size:20px;font-weight:700;color:var(--primary);">{{ r.total_score.toFixed(1) }}</div>
								<div v-if="r.recommendation" style="font-size:12px;" :style="getRecStyle(r.recommendation)">{{ r.recommendation }}</div>
								<div v-if="r.status === '待评审'" style="font-size:12px;color:var(--warning);font-weight:500;">待评审 &gt;</div>
								<button v-if="r.status === '已跳过'" class="sg-btn-unskip" @click.stop="unskipReview(r)">撤销跳过</button>
							</div>
						</div>
					</div>
				</div>
			</template>

			<!-- ===== 排行榜 ===== -->
			<template v-if="mainTab === 'leaderboard'">
				<div class="sg-sub-tabs">
					<div class="sg-sub-tab" :class="{ active: lbTab === 'monthly' }" @click="switchLbTab('monthly')">月度</div>
					<div class="sg-sub-tab" :class="{ active: lbTab === 'yearly' }" @click="switchLbTab('yearly')">年度</div>
					<div class="sg-sub-tab" :class="{ active: lbTab === 'all' }" @click="switchLbTab('all')">总排行</div>
				</div>

				<div class="sg-lb-my-stats">
					<span>本月获得 <strong>{{ myStats.monthly_earned || 0 }}</strong></span>
					<span>总获得 <strong>{{ myStats.total_earned || 0 }}</strong></span>
				</div>

				<div v-if="contentLoading" class="sg-loading"><div class="sg-spinner"></div><p>加载中...</p></div>
				<template v-else>
					<div v-if="!leaderboard.length" class="sg-empty"><div class="sg-empty-icon">&#127942;</div>暂无排行数据</div>
					<div v-for="item in leaderboard" :key="item.user" class="sg-rank-item">
						<div class="sg-rank-num" :class="getRankClass(item.rank)">{{ item.rank }}</div>
						<img v-if="item.user_image" class="sg-rank-avatar" :src="item.user_image" :alt="item.full_name" />
						<div v-else class="sg-rank-avatar" style="background:var(--primary);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:bold;">{{ (item.full_name || item.user || '?').charAt(0) }}</div>
						<div class="sg-rank-info">
							<div class="sg-rank-name">{{ item.full_name || item.user }}</div>
							<div class="sg-rank-dept" v-if="item.department">{{ item.department }}</div>
						</div>
						<div class="sg-rank-points">{{ item.total_points || 0 }}</div>
					</div>
				</template>
			</template>

			<!-- ===== 积分兑换 ===== -->
			<template v-if="mainTab === 'redemption'">
				<div class="sg-sub-tabs">
					<div class="sg-sub-tab" :class="{ active: redTab === 'shop' }" @click="switchRedTab('shop')">可兑换</div>
					<div class="sg-sub-tab" :class="{ active: redTab === 'mine' }" @click="switchRedTab('mine')">我的兑换</div>
				</div>

				<div v-if="contentLoading" class="sg-loading"><div class="sg-spinner"></div><p>加载中...</p></div>

				<template v-else-if="redTab === 'shop'">
					<div v-if="!redemptionItems.length" class="sg-empty"><div class="sg-empty-icon">&#128230;</div>暂无可兑换商品</div>
					<div v-else class="sg-items">
						<div v-for="item in redemptionItems" :key="item.name" class="sg-item-card">
							<img v-if="item.image" :src="item.image" class="sg-item-img" />
							<div v-else class="sg-item-img" style="display:flex;align-items:center;justify-content:center;font-size:48px;">&#127873;</div>
							<div class="sg-item-body">
								<div class="sg-item-name">{{ item.item_name }}</div>
								<div v-if="item.description" class="sg-item-desc">{{ item.description }}</div>
								<div class="sg-item-footer">
									<div>
										<span class="sg-item-points">{{ item.points_required }}</span>
										<span style="font-size:12px;color:var(--text-3);"> 积分</span>
										<span v-if="item.stock > 0" class="sg-item-stock" style="margin-left:8px;">库存: {{ item.stock }}</span>
										<span v-else class="sg-item-stock" style="margin-left:8px;">不限</span>
									</div>
									<button class="sg-btn sg-btn-primary" style="width:auto;padding:6px 16px;font-size:13px;"
										:disabled="redeeming === item.name || (myStats.balance || 0) < item.points_required"
										@click="handleRedeem(item)">
										{{ redeeming === item.name ? '兑换中...' : '兑换' }}
									</button>
								</div>
							</div>
						</div>
					</div>
				</template>

				<template v-else>
					<div v-if="!myRedemptions.length" class="sg-empty"><div class="sg-empty-icon">&#128203;</div>暂无兑换记录</div>
					<div v-else style="padding:0 0 16px;">
						<div v-for="r in myRedemptions" :key="r.name" class="sg-card" style="margin-bottom:8px;">
							<div style="display:flex;justify-content:space-between;align-items:center;">
								<div>
									<div style="font-size:15px;font-weight:500;color:var(--text-1);">{{ r.item_name }}</div>
									<div style="font-size:12px;color:var(--text-3);margin-top:4px;">{{ formatDate(r.redemption_date) }} · x{{ r.quantity }}</div>
								</div>
								<div style="text-align:right;">
									<div style="font-size:16px;font-weight:bold;color:var(--warning);">-{{ r.points_cost }}</div>
									<span class="sg-status-pill" :style="getStatusStyle(r.status)">{{ getStatusText(r.status) }}</span>
								</div>
							</div>
						</div>
					</div>
				</template>
			</template>
		</div>

		<!-- ===== 评审弹窗 ===== -->
		<div v-if="showReviewModal" class="sg-modal-overlay" @click.self="closeReviewModal">
			<div class="sg-modal">
				<div class="sg-modal-header">
					<span class="sg-modal-title">评审建议</span>
					<span class="sg-modal-close" @click="closeReviewModal">&times;</span>
				</div>
				<div v-if="modalLoading" class="sg-loading" style="padding:40px 0;"><div class="sg-spinner"></div></div>
				<div v-else class="sg-modal-body">
					<!-- 建议信息 -->
					<div class="sg-review-suggestion-info">
						<div style="font-size:18px;font-weight:700;color:var(--text-1);line-height:1.4;">{{ reviewDetail.suggestion.title }}</div>
						<div style="display:flex;gap:8px;margin-top:8px;flex-wrap:wrap;">
							<span class="sg-badge sg-badge-purple">{{ reviewDetail.suggestion.category }}</span>
							<span style="font-size:12px;color:var(--text-3);">建议人: {{ reviewDetail.suggestion.submitted_by }}</span>
						</div>
						<div v-if="reviewDetail.suggestion.description" style="margin-top:12px;font-size:14px;color:var(--text-2);line-height:1.6;background:var(--bg-2);padding:14px;border-radius:var(--radius-md);" v-html="reviewDetail.suggestion.description"></div>
						<div v-if="reviewDetail.attachments && reviewDetail.attachments.length > 0" style="margin-top:12px;">
							<div style="font-size:13px;font-weight:500;color:var(--text-2);margin-bottom:8px;">附件图片</div>
							<div class="sg-image-list">
								<div v-for="(att, i) in reviewDetail.attachments" :key="i" class="sg-image-item" style="width:72px;height:72px;cursor:pointer;" @click="previewImage(att.file_url)">
									<img :src="att.file_url" />
								</div>
							</div>
						</div>
					</div>
					<div style="height:1px;background:var(--border);margin:20px 0;"></div>

					<!-- 打分 -->
					<div v-if="isReviewPending">
						<div style="font-size:15px;font-weight:600;color:var(--text-1);margin-bottom:16px;">维度打分</div>
						<div v-for="dim in dimensions" :key="dim.name" class="sg-score-row">
							<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
								<div>
									<span style="font-size:14px;font-weight:500;color:var(--text-1);">{{ dim.dimension_name }}</span>
									<span style="font-size:12px;color:var(--text-3);margin-left:4px;">({{ dim.weight }}%)</span>
								</div>
								<div style="font-size:16px;font-weight:700;color:var(--primary);">{{ getScore(dim.name) }}<span style="font-size:12px;color:var(--text-3);font-weight:400;">/{{ dim.max_score }}</span></div>
							</div>
							<div v-if="dim.description" style="font-size:12px;color:var(--text-3);margin-bottom:6px;">{{ dim.description }}</div>
							<div class="sg-score-slider-wrap">
								<input type="range" class="sg-score-slider" :min="0" :max="dim.max_score" :step="1" :value="getScore(dim.name)" @input="setScore(dim.name, $event.target.value)" />
								<div class="sg-score-labels"><span>0</span><span>{{ dim.max_score }}</span></div>
							</div>
						</div>
						<div style="margin-top:20px;">
							<div class="sg-form-label">评审意见</div>
							<textarea class="sg-textarea" v-model="reviewComment" placeholder="请输入您的评审意见..." style="min-height:100px;"></textarea>
						</div>
						<div style="margin-top:16px;">
							<div class="sg-form-label">综合建议</div>
							<div class="sg-rec-options">
								<div class="sg-rec-option" :class="{ active: reviewRecommendation === '建议采纳' }" @click="reviewRecommendation = '建议采纳'">
									<span style="font-size:20px;">&#10003;</span><span>建议采纳</span>
								</div>
								<div class="sg-rec-option" :class="{ active: reviewRecommendation === '待定' }" @click="reviewRecommendation = '待定'">
									<span style="font-size:20px;">?</span><span>待定</span>
								</div>
								<div class="sg-rec-option reject" :class="{ active: reviewRecommendation === '建议拒绝' }" @click="reviewRecommendation = '建议拒绝'">
									<span style="font-size:20px;">&#10007;</span><span>建议拒绝</span>
								</div>
							</div>
						</div>
						<div style="margin-top:24px;display:flex;gap:10px;">
							<button class="sg-btn sg-btn-outline" style="flex:1;" @click="skipCurrentReview">跳过</button>
							<button class="sg-btn sg-btn-primary" style="flex:2;" :disabled="submitting" @click="submitCurrentReview">
								{{ submitting ? '提交中...' : '提交评审' }}
							</button>
						</div>
					</div>

					<!-- 已评审结果 -->
					<div v-else>
						<div style="font-size:15px;font-weight:600;color:var(--text-1);margin-bottom:16px;">评审结果</div>
						<div v-for="row in reviewDetail.review.scores" :key="row.name" class="sg-score-row">
							<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">
								<span style="font-size:14px;color:var(--text-1);">{{ row.dimension }}</span>
								<div>
									<span style="font-size:16px;font-weight:700;color:var(--primary);">{{ row.score }}</span>
									<span style="font-size:12px;color:var(--text-3);">/{{ row.max_score || 10 }}</span>
									<span style="font-size:12px;color:var(--text-3);margin-left:6px;">权重 {{ row.weight }}%</span>
								</div>
							</div>
							<div class="sg-score-bar-bg">
								<div class="sg-score-bar" :style="{ width: (row.score / (row.max_score || 10) * 100) + '%' }"></div>
							</div>
						</div>
						<div style="margin-top:16px;padding:14px;background:var(--primary-light);border-radius:var(--radius-md);text-align:center;">
							<div style="font-size:13px;color:var(--text-3);">加权总分</div>
							<div style="font-size:28px;font-weight:700;color:var(--primary);">{{ (reviewDetail.review.total_score || 0).toFixed(2) }}</div>
						</div>
						<div v-if="reviewDetail.review.comment" style="margin-top:14px;font-size:14px;color:var(--text-2);line-height:1.6;background:var(--bg-2);padding:14px;border-radius:var(--radius-md);">
							<div style="font-size:12px;font-weight:500;color:var(--text-3);margin-bottom:4px;">评审意见</div>
							{{ reviewDetail.review.comment }}
						</div>
						<div v-if="reviewDetail.review.recommendation" style="margin-top:10px;text-align:center;">
							<span class="sg-badge" :style="getRecStyle(reviewDetail.review.recommendation)">{{ reviewDetail.review.recommendation }}</span>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- ===== 建议详情弹窗 ===== -->
		<div v-if="showSuggestionModal" class="sg-modal-overlay" @click.self="closeSuggestionModal">
			<div class="sg-modal">
				<div class="sg-modal-header">
					<span class="sg-modal-title">建议详情</span>
					<span class="sg-modal-close" @click="closeSuggestionModal">&times;</span>
				</div>
				<div v-if="suggestionDetailLoading" class="sg-loading" style="padding:40px 0;"><div class="sg-spinner"></div></div>
				<div v-else class="sg-modal-body">
					<div style="font-size:18px;font-weight:700;color:var(--text-1);line-height:1.4;">{{ suggestionDetail.title }}</div>
					<div style="display:flex;gap:8px;margin-top:8px;flex-wrap:wrap;">
						<span class="sg-badge sg-badge-purple">{{ suggestionDetail.category }}</span>
						<span class="sg-status-pill" :style="getStatusStyle(suggestionDetail.status)">{{ getStatusText(suggestionDetail.status) }}</span>
					</div>
					<div v-if="suggestionDetail.description" style="margin-top:12px;font-size:14px;color:var(--text-2);line-height:1.6;background:var(--bg-2);padding:14px;border-radius:var(--radius-md);" v-html="suggestionDetail.description"></div>
					<div v-if="suggestionAttachments && suggestionAttachments.length > 0" style="margin-top:12px;">
						<div style="font-size:13px;font-weight:500;color:var(--text-2);margin-bottom:8px;">附件图片</div>
						<div class="sg-image-list">
							<div v-for="(att, i) in suggestionAttachments" :key="i" class="sg-image-item" style="width:72px;height:72px;cursor:pointer;" @click="previewImage(att.file_url)">
								<img :src="att.file_url" />
							</div>
						</div>
					</div>
					<div v-if="suggestionDetail.total_score != null" style="margin-top:16px;padding:14px;background:var(--primary-light);border-radius:var(--radius-md);text-align:center;">
						<div style="font-size:13px;color:var(--text-3);">综合得分</div>
						<div style="font-size:28px;font-weight:700;color:var(--primary);">{{ suggestionDetail.total_score }}</div>
					</div>
					<div style="margin-top:16px;font-size:12px;color:var(--text-3);">
						提交时间：{{ formatDate(suggestionDetail.creation) }}
					</div>
				</div>
			</div>
		</div>

		<!-- 图片预览 -->
		<div v-if="previewingImage" class="sg-image-preview-overlay" @click="previewingImage = null">
			<img :src="previewingImage" class="sg-image-preview-img" @click.stop />
		</div>
	</div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';

// === 状态 ===
const mainTab = ref('suggestions');
const subTab = ref('my_suggestions');
const reviewTab = ref('pending');
const lbTab = ref('monthly');
const redTab = ref('shop');
const contentLoading = ref(false);

// 用户信息
const userName = ref(frappe.session.user_fullname || frappe.session.user);
const userInitial = computed(() => (userName.value || '?').charAt(0));
const roleLabel = ref('员工');

// 统计
const myStats = ref({});
const reviewStats = ref({ total: 0, pending: 0, completed: 0, skipped: 0 });

// 我的建议
const suggestions = ref([]);
const pointLogs = ref([]);
const myRedemptions = ref([]);
const suggestionDetail = ref({});
const showSuggestionModal = ref(false);

// 评审
const reviews = ref([]);
const dimensions = ref([]);
const showReviewModal = ref(false);
const modalLoading = ref(false);
const reviewDetail = ref({ suggestion: {}, review: {}, attachments: [] });
const currentReviewName = ref('');
const scoreMap = ref({});
const reviewComment = ref('');
const reviewRecommendation = ref('待定');
const submitting = ref(false);

// 排行榜
const leaderboard = ref([]);

// 兑换
const redemptionItems = ref([]);
const redeeming = ref(null);

// 图片预览
const previewingImage = ref(null);

const isReviewPending = computed(() => reviewDetail.value.review?.status === '待评审');

const reviewEmptyText = computed(() => {
	const map = { pending: '没有待评审的建议', completed: '还没有评审记录', skipped: '没有跳过的评审' };
	return map[reviewTab.value] || '暂无数据';
});

onMounted(async () => {
	await Promise.all([loadMyStats(), loadReviewStats(), loadDimensions()]);
	await loadMainTabData();
});

// === 统计加载 ===
async function loadMyStats() {
	try { myStats.value = await frappe.xcall('suggestion_system.api.points.get_my_stats'); } catch(e) {}
}
async function loadReviewStats() {
	try { reviewStats.value = await frappe.xcall('suggestion_system.api.review.get_review_stats'); } catch(e) {}
}
async function loadDimensions() {
	try { dimensions.value = await frappe.xcall('suggestion_system.api.review.get_review_dimensions'); } catch(e) {}
}

// === Tab 切换 ===
async function switchMainTab(tab) {
	mainTab.value = tab;
	await loadMainTabData();
}

async function loadMainTabData() {
	contentLoading.value = true;
	try {
		if (mainTab.value === 'suggestions') {
			await loadSubTabData();
		} else if (mainTab.value === 'review') {
			await loadReviews();
		} else if (mainTab.value === 'leaderboard') {
			await loadLeaderboard();
		} else if (mainTab.value === 'redemption') {
			await loadRedemptionData();
		}
	} finally {
		contentLoading.value = false;
	}
}

async function switchSubTab(tab) {
	if (subTab.value === tab) return;
	subTab.value = tab;
	contentLoading.value = true;
	try { await loadSubTabData(); } finally { contentLoading.value = false; }
}

async function loadSubTabData() {
	if (subTab.value === 'my_suggestions') {
		suggestions.value = await frappe.xcall('suggestion_system.api.collection.get_my_suggestions', { limit: 50 });
	} else if (subTab.value === 'points') {
		pointLogs.value = await frappe.xcall('suggestion_system.api.points.get_points_history', { limit: 50 });
	} else if (subTab.value === 'my_redemptions') {
		myRedemptions.value = await frappe.xcall('suggestion_system.api.redemption.get_my_redemptions', { limit: 50 });
	}
}

async function switchReviewTab(tab) {
	if (reviewTab.value === tab) return;
	reviewTab.value = tab;
	contentLoading.value = true;
	try { await loadReviews(); } finally { contentLoading.value = false; }
}

async function loadReviews() {
	const statusMap = { pending: '待评审', completed: '已评审', skipped: '已跳过' };
	reviews.value = await frappe.xcall('suggestion_system.api.review.get_my_reviews', {
		status: statusMap[reviewTab.value], limit: 50,
	});
}

async function switchLbTab(tab) {
	if (lbTab.value === tab) return;
	lbTab.value = tab;
	contentLoading.value = true;
	try { await loadLeaderboard(); } finally { contentLoading.value = false; }
}

async function loadLeaderboard() {
	leaderboard.value = await frappe.xcall('suggestion_system.api.points.get_leaderboard', {
		period: lbTab.value, limit: 50,
	});
}

async function switchRedTab(tab) {
	if (redTab.value === tab) return;
	redTab.value = tab;
	contentLoading.value = true;
	try { await loadRedemptionData(); } finally { contentLoading.value = false; }
}

async function loadRedemptionData() {
	if (redTab.value === 'shop') {
		redemptionItems.value = await frappe.xcall('suggestion_system.api.redemption.get_redemption_items');
	} else {
		myRedemptions.value = await frappe.xcall('suggestion_system.api.redemption.get_my_redemptions');
	}
}

// === 建议详情 ===
const suggestionAttachments = ref([]);
const suggestionDetailLoading = ref(false);

async function openSuggestionDetail(s) {
	suggestionDetail.value = { title: s.title, category: s.category, status: s.status, creation: s.creation };
	suggestionAttachments.value = [];
	showSuggestionModal.value = true;
	suggestionDetailLoading.value = true;
	try {
		const data = await frappe.xcall('suggestion_system.api.collection.get_suggestion_detail', { name: s.name });
		suggestionDetail.value = data.suggestion;
		suggestionAttachments.value = data.attachments || [];
	} catch(e) {
		frappe.show_alert({ message: '加载详情失败', indicator: 'red' });
	} finally {
		suggestionDetailLoading.value = false;
	}
}

function closeSuggestionModal() {
	showSuggestionModal.value = false;
}

// === 评审操作 ===
async function openReview(r) {
	currentReviewName.value = r.name;
	showReviewModal.value = true;
	modalLoading.value = true;
	try {
		reviewDetail.value = await frappe.xcall('suggestion_system.api.review.get_review_detail', { name: r.name });
		if (reviewDetail.value.review?.status === '待评审') {
			scoreMap.value = {};
			for (const row of reviewDetail.value.review.scores) {
				scoreMap.value[row.dimension] = row.score || 0;
			}
			reviewComment.value = '';
			reviewRecommendation.value = '待定';
		}
	} catch(e) {
		frappe.show_alert({ message: '加载详情失败', indicator: 'red' });
	} finally {
		modalLoading.value = false;
	}
}
function closeReviewModal() { showReviewModal.value = false; }

function getScore(dimensionName) { return scoreMap.value[dimensionName] ?? 0; }
function setScore(dimensionName, val) { scoreMap.value[dimensionName] = parseInt(val) || 0; }

async function submitCurrentReview() {
	if (!reviewRecommendation.value) {
		frappe.show_alert({ message: '请选择综合建议', indicator: 'orange' }); return;
	}
	if (Object.values(scoreMap.value).every(v => v === 0)) {
		frappe.show_alert({ message: '请至少为一个维度打分', indicator: 'orange' }); return;
	}
	submitting.value = true;
	try {
		const scores = Object.entries(scoreMap.value).map(([dimension, score]) => ({ dimension, score }));
		await frappe.xcall('suggestion_system.api.review.submit_review', {
			name: currentReviewName.value,
			scores: JSON.stringify(scores),
			comment: reviewComment.value || undefined,
			recommendation: reviewRecommendation.value,
		});
		frappe.show_alert({ message: '评审提交成功', indicator: 'green' });
		closeReviewModal();
		await refreshAll();
	} catch(e) {
		frappe.show_alert({ message: e.message || '提交失败', indicator: 'red' });
	} finally {
		submitting.value = false;
	}
}

async function skipCurrentReview() {
	if (!confirm('确定跳过此评审？')) return;
	try {
		await frappe.xcall('suggestion_system.api.review.skip_review', { name: currentReviewName.value });
		frappe.show_alert({ message: '已跳过', indicator: 'blue' });
		closeReviewModal();
		await refreshAll();
	} catch(e) {
		frappe.show_alert({ message: '操作失败', indicator: 'red' });
	}
}

async function unskipReview(r) {
	if (!confirm('确定撤销跳过，恢复为待评审？')) return;
	try {
		await frappe.xcall('suggestion_system.api.review.unskip_review', { name: r.name });
		frappe.show_alert({ message: '已恢复为待评审', indicator: 'green' });
		await refreshAll();
	} catch(e) {
		frappe.show_alert({ message: e.message || '操作失败', indicator: 'red' });
	}
}

// === 兑换 ===
async function handleRedeem(item) {
	if ((myStats.value.balance || 0) < item.points_required) {
		frappe.show_alert({ message: '积分不足', indicator: 'red' }); return;
	}
	if (!confirm(`确认兑换「${item.item_name}」？\n消耗 ${item.points_required} 积分`)) return;
	redeeming.value = item.name;
	try {
		const result = await frappe.xcall('suggestion_system.api.redemption.create_redemption', {
			item: item.name, quantity: 1,
		});
		frappe.show_alert({ message: `兑换成功！消耗 ${result.points_cost} 积分`, indicator: 'green' });
		await refreshAll();
		await switchRedTab('shop');
	} catch(e) {
		frappe.show_alert({ message: e.message || '兑换失败', indicator: 'red' });
	} finally {
		redeeming.value = null;
	}
}

// === 刷新 ===
async function refreshAll() {
	await Promise.all([loadMyStats(), loadReviewStats()]);
	await loadMainTabData();
}

// === 图片预览 ===
function previewImage(url) { previewingImage.value = url; }

// === 工具函数 ===
function formatDate(dateStr) {
	if (!dateStr) return '';
	return new Date(dateStr).toLocaleString('zh-CN', {
		year: 'numeric', month: '2-digit', day: '2-digit',
		hour: '2-digit', minute: '2-digit',
	});
}

function getStatusText(status) {
	const map = { '已提交':'已提交','评审中':'评审中','已评审':'已评审','已通过':'已通过','已采纳':'已采纳','已拒绝':'已拒绝','已实施':'已实施','已关闭':'已关闭','待处理':'待处理','已发放':'已发放','已取消':'已取消' };
	return map[status] || status || '';
}

function getStatusStyle(status) {
	const map = {
		'已提交':'background:var(--info-light);color:var(--info);',
		'评审中':'background:var(--warning-light);color:var(--warning);',
		'已评审':'background:var(--info-light);color:var(--info);',
		'已采纳':'background:var(--success-light);color:var(--success);',
		'已拒绝':'background:var(--danger-light);color:var(--danger);',
		'已实施':'background:var(--success-light);color:var(--success);',
		'待处理':'background:var(--warning-light);color:var(--warning);',
		'已发放':'background:var(--success-light);color:var(--success);',
		'已取消':'background:var(--bg-3);color:var(--text-3);',
	};
	return (map[status] || 'background:var(--bg-3);color:var(--text-3);') + 'padding:3px 10px;border-radius:999px;font-weight:500;font-size:12px;white-space:nowrap;';
}

function getRecStyle(rec) {
	const map = {
		'建议采纳':'background:var(--success-light);color:var(--success);',
		'建议拒绝':'background:var(--danger-light);color:var(--danger);',
		'待定':'background:var(--warning-light);color:var(--warning);',
	};
	return (map[rec] || 'background:var(--bg-3);color:var(--text-3);') + 'padding:3px 10px;border-radius:999px;font-weight:500;';
}

function getRankClass(rank) {
	if (rank === 1) return 'top-1';
	if (rank === 2) return 'top-2';
	if (rank === 3) return 'top-3';
	return 'other';
}
</script>
